import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from "@mui/material";
import Editor from "@monaco-editor/react";

const TEMPLATE_VARIABLES = [
  { name: "productName", description: "Product name" },
  { name: "companyName", description: "Company name" },
  { name: "brandName", description: "Brand name" },
  { name: "primaryPackaging", description: "Primary packaging type" },
  { name: "skuWt", description: "SKU weight" },
  { name: "skuWtUnit", description: "SKU weight unit" },
  { name: "marketPositioning", description: "Market positioning" },
  { name: "angle", description: "Camera angle" },
  { name: "background", description: "Background description" },
];

const DALL_E_MODELS = [
  { value: "dall-e-2", label: "DALL-E 2" },
  { value: "dall-e-3", label: "DALL-E 3" },
];

const IMAGE_SIZES = {
  "dall-e-2": ["256x256", "512x512", "1024x1024"],
  "dall-e-3": ["1024x1024", "1024x1792", "1792x1024"],
};

const QUALITY_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "hd", label: "HD (DALL-E 3 only)" },
];

export default function ImageSchemaEditor({ schema, onSave, onCancel }) {
  const [promptTemplate, setPromptTemplate] = useState("");
  const [model, setModel] = useState("dall-e-3");
  const [size, setSize] = useState("1024x1024");
  const [quality, setQuality] = useState("standard");
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (schema) {
      setPromptTemplate(schema.prompt_template || "");
      setModel(schema.model || "dall-e-3");
      setSize(schema.size || "1024x1024");
      setQuality(schema.quality || "standard");
      setDescription(schema.description || "");
    }
  }, [schema]);

  useEffect(() => {
    // Generate preview with sample values
    const sampleValues = {
      productName: "Chocolate Chip Cookies",
      companyName: "Acme Foods",
      brandName: "Delicious",
      primaryPackaging: "Flexible Pouch",
      skuWt: "200",
      skuWtUnit: "g",
      marketPositioning: "Premium",
      angle: "45-degree",
      background: "clean white studio",
    };

    let rendered = promptTemplate;
    for (const [key, value] of Object.entries(sampleValues)) {
      rendered = rendered.replace(new RegExp(`\\{${key}\\}`, "g"), value);
    }
    setPreview(rendered);
  }, [promptTemplate]);

  const insertVariable = (variableName) => {
    setPromptTemplate((prev) => prev + `{${variableName}}`);
  };

  const handleSave = () => {
    if (!promptTemplate.trim()) {
      alert("Prompt template is required");
      return;
    }

    onSave({
      prompt_template: promptTemplate,
      model,
      size,
      quality,
      description,
    });
  };

  return (
    <Box>
      <DialogTitle>
        {schema ? `Edit Image Schema v${schema.version}` : "Create New Image Schema"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this schema version"
            fullWidth
            size="small"
          />

          {/* Model Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Model</InputLabel>
            <Select value={model} onChange={(e) => setModel(e.target.value)} label="Model">
              {DALL_E_MODELS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Size Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Image Size</InputLabel>
            <Select value={size} onChange={(e) => setSize(e.target.value)} label="Image Size">
              {IMAGE_SIZES[model].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quality Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>Quality</InputLabel>
            <Select
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              label="Quality"
              disabled={model === "dall-e-2"}
            >
              {QUALITY_OPTIONS.map((q) => (
                <MenuItem
                  key={q.value}
                  value={q.value}
                  disabled={q.value === "hd" && model === "dall-e-2"}
                >
                  {q.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Template Variables */}
          <Box>
            <Typography variant="body2" gutterBottom fontWeight="bold">
              Available Variables (click to insert):
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {TEMPLATE_VARIABLES.map((v) => (
                <Chip
                  key={v.name}
                  label={`{${v.name}}`}
                  size="small"
                  onClick={() => insertVariable(v.name)}
                  sx={{ cursor: "pointer" }}
                  title={v.description}
                />
              ))}
            </Stack>
          </Box>

          {/* Prompt Template Editor */}
          <Box>
            <Typography variant="body2" gutterBottom fontWeight="bold">
              Prompt Template:
            </Typography>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Editor
                height="250px"
                defaultLanguage="plaintext"
                value={promptTemplate}
                onChange={(value) => setPromptTemplate(value || "")}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: "off",
                  wordWrap: "on",
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                }}
              />
            </Box>
          </Box>

          {/* Live Preview */}
          <Box>
            <Typography variant="body2" gutterBottom fontWeight="bold">
              Preview (with sample values):
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "grey.50",
                maxHeight: 200,
                overflow: "auto",
                fontSize: "0.875rem",
              }}
            >
              {preview || <em>Preview will appear here...</em>}
            </Paper>
          </Box>

          {schema?.is_active && (
            <Alert severity="warning">
              This schema is currently active. Create a new version to make changes.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={schema?.is_active}>
          {schema ? "Save Changes" : "Create Version"}
        </Button>
      </DialogActions>
    </Box>
  );
}
